<script setup lang="ts">
import { useFormat } from '@/composables/format';
import Datepicker, { type DatePickerInstance } from '@vuepic/vue-datepicker';
import { ErrorMessage, Field } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useLocale } from '../../composables/locale';
import { toLocalMidnightDate } from '../../utils/temporalUtils';
import BackstageInputLabel from './BackstageInputLabel.vue';

export interface Props {
  name: string;
  label?: string;
  placeholder?: string;
  modelValue?: Date | string;
  readOnlyValue?: any;
  disabled?: boolean;
  readOnly?: boolean;
  type?: string;
  tabIndex?: string;
  autocomplete?: string;
  required?: boolean;
  disabledDates?: (date: Date) => boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  placeholder: '',
  readOnlyValue: '',
  disabled: false,
  readOnly: false,
  type: 'text',
  required: false,
});

const emit = defineEmits<{
  change: [];
  'update:modelValue': [value: Date];
}>();

const touched = ref(false);
const inputFocused = ref(false);
const input = ref<HTMLInputElement | null>(null);

const { userLocale } = useLocale();
const { formatDateNoUTC } = useFormat();
const datepicker = ref<DatePickerInstance>(null);
const refreshLocale = ref(0);

function normalizeDate(value: Date | string | undefined) {
  if (value instanceof Date) {
    return toLocalMidnightDate(value);
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return toLocalMidnightDate(value);
  }

  return value;
}

// Normalize YYYY-MM-DD strings to local midnight Date to avoid UTC timezone shift
const normalizedModelValue = computed(() => normalizeDate(props.modelValue));
const normalizedMinDate = computed(() => normalizeDate(props.minDate));
const normalizedMaxDate = computed(() => normalizeDate(props.maxDate));

// computed properties

const labelIsPlaceHolder = computed<boolean>(() => {
  return props.placeholder === '' && !props.modelValue && !inputFocused.value && !props.readOnly && !props.disabled;
});

watch(userLocale, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    refreshLocale.value++;
  }
});

// methods

function focusOnInput() {
  inputFocused.value = true;
  input.value?.focus();
  datepicker.value?.openMenu();
}

function updateValue(selectedDate: Date) {
  emit('update:modelValue', selectedDate);
}
// function isDark() {
//   return userProfile.value?.user?.theme === BackstageTheme.dark;
// }
</script>
<template>
  <BackstageInputLabel
    :class="[{ placeholder: labelIsPlaceHolder, disabled: disabled, 'read-only': readOnly }]"
    :label="label"
    :label-for="name ? `dp-input-${name}` : undefined"
    :required="required"
    v-on:focus="focusOnInput"
    v-on:click="focusOnInput"
    v-bind="$attrs">
    <template #tooltip>
      <slot name="tooltip"></slot>
    </template>
    <span v-if="readOnly || disabled"> {{ formatDateNoUTC(normalizedModelValue as Date) }}</span>
    <Field
      v-else
      :name="name"
      v-slot="{ field }"
      :model-value="modelValue"
      :validate-on-input="true"
      @blur="touched = true">
      <!-- we could either change disabled-dates with min-date or at least also add it as an option -->
      <Datepicker
        v-bind="field"
        ref="datepicker"
        :key="refreshLocale"
        :uid="name"
        :name="name"
        :model-value="normalizedModelValue"
        :locale="userLocale"
        :tabindex="tabIndex"
        :minimumView="'day'"
        :disabled-dates="disabledDates"
        :min-date="normalizedMinDate"
        :max-date="normalizedMaxDate"
        :enable-time-picker="false"
        :format="formatDateNoUTC"
        :placeholder="placeholder"
        :month-change-on-scroll="false"
        :auto-apply="true"
        @update:model-value="updateValue" />
    </Field>
  </BackstageInputLabel>
  <ErrorMessage v-if="name && touched" :name="name" />
</template>

<style lang="scss" scoped>
.disabled {
  color: var(--field-text-disabled-color);
}
</style>
